import { useState } from "react";
import { List, useTable } from "@refinedev/antd";
import { useGetIdentity, useUpdate, type HttpError } from "@refinedev/core";
import { Button, Form, Input, Modal, Select, Table, Tag, Typography, message, Space } from "antd";

import { DeletionCountdown } from "../../components/DeletionCountdown";
import { request } from "../../lib/http";
import { ROLES, roleMeta } from "../../lib/constants";

interface User {
  id: string;
  username?: string;
  email?: string;
  role?: string;
  blocked?: boolean;
  createdAt?: string;
  /** Когда учётная запись будет удалена по просьбе владельца. */
  deletionScheduledAt?: string;
}

interface UserFilters {
  search?: string;
  role?: string;
}

export function UserList() {
  const { tableProps, searchFormProps, tableQuery } = useTable<User, HttpError, UserFilters>({
    resource: "users",
    onSearch: (values) => [
      { field: "search", operator: "contains", value: values.search },
      { field: "role", operator: "eq", value: values.role },
    ],
    sorters: { mode: "off" },
  });

  const { data: identity } = useGetIdentity<{ id: string; role: string }>();
  // В этой версии Refine хук возвращает саму мутацию отдельным полем.
  const { mutate: updateUser, mutation } = useUpdate();

  // Роли меняет только администратор: модератору backend такой метод не даёт,
  // и показывать ему неработающий селект бессмысленно.
  const canChangeRoles = identity?.role === "ROLE_ADMIN";
  // Блокировка и удаление — решение владельца проекта, а не модератора:
  // backend их модератору и не даёт.
  const [busy, setBusy] = useState<string | null>(null);

  const setBlocked = async (user: User, blocked: boolean) => {
    setBusy(user.id);
    try {
      await request(`/api/v1/admin/users/${user.id}/blocked`, {
        method: "POST",
        body: { blocked },
      });
      message.success(blocked ? `${user.username} заблокирован` : `${user.username} разблокирован`);
      void tableQuery.refetch();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Не удалось изменить блокировку");
    } finally {
      setBusy(null);
    }
  };

  // Удаление необратимо и уносит с собой ответы, очки и переписку человека,
  // поэтому подтверждение спрашивает имя, а не просто «вы уверены».
  const remove = (user: User) => {
    Modal.confirm({
      title: `Удалить ${user.username}?`,
      content:
        "Удаление необратимо: вместе с учётной записью исчезнут его ответы, очки, избранное и переписка. " +
        "Созданные им квесты останутся, но без автора. Если нужно просто закрыть вход — заблокируйте.",
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        await request(`/api/v1/admin/users/${user.id}`, { method: "DELETE" });
        message.success(`${user.username} удалён`);
        void tableQuery.refetch();
      },
    });
  };

  const changeRole = (user: User, role: string) => {
    updateUser(
      { resource: "users", id: user.id, values: { role } },
      {
        onSuccess: () => message.success(`Роль ${user.username} обновлена`),
        onError: (error) => message.error(error.message),
      },
    );
  };

  return (
    <List title="Пользователи" canCreate={false}>
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 8 }}
        onValuesChange={() => searchFormProps.form?.submit()}
      >
        <Form.Item name="search">
          <Input.Search placeholder="Поиск по имени или email" allowClear style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="role">
          <Select
            placeholder="Роль"
            allowClear
            style={{ width: 200 }}
            options={ROLES.map((item) => ({ value: item.value, label: item.label }))}
          />
        </Form.Item>
      </Form>

      <Table {...tableProps} rowKey="id">
        <Table.Column<User>
          title="Пользователь"
          dataIndex="username"
          render={(username: string, record: User) => (
            <Space>
              <Typography.Text delete={record.blocked}>{username}</Typography.Text>
              {record.blocked && <Tag color="red">Заблокирован</Tag>}
              {record.deletionScheduledAt && <DeletionCountdown until={record.deletionScheduledAt} />}
            </Space>
          )}
        />
        <Table.Column<User> title="Email" dataIndex="email" />

        <Table.Column<User>
          title="Роль"
          dataIndex="role"
          width={240}
          render={(role?: string, record?: User) => {
            const meta = roleMeta(role);
            const isSelf = record?.id === identity?.id;

            if (!canChangeRoles || isSelf) {
              return (
                <Space>
                  {meta ? <Tag color={meta.color}>{meta.label}</Tag> : "—"}
                  {isSelf && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      это вы
                    </Typography.Text>
                  )}
                </Space>
              );
            }

            return (
              <Select
                value={role}
                style={{ width: 200 }}
                loading={mutation.isPending}
                options={ROLES.map((item) => ({ value: item.value, label: item.label }))}
                onChange={(value) => record && changeRole(record, value)}
              />
            );
          }}
        />
        {canChangeRoles && (
          <Table.Column<User>
            title="Доступ"
            width={220}
            render={(_, record) => {
              if (record.id === identity?.id) {
                return (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    это вы
                  </Typography.Text>
                );
              }
              return (
                <Space>
                  <Button
                    size="small"
                    loading={busy === record.id}
                    onClick={() => void setBlocked(record, !record.blocked)}
                  >
                    {record.blocked ? "Разблокировать" : "Заблокировать"}
                  </Button>
                  <Button size="small" danger onClick={() => remove(record)}>
                    Удалить
                  </Button>
                </Space>
              );
            }}
          />
        )}
      </Table>

      {!canChangeRoles && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
          Менять роли, блокировать и удалять пользователей может только администратор.
        </Typography.Paragraph>
      )}
    </List>
  );
}
